import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { environment } from '../../environment';
import { ESRI } from '../../esri-loader-util';
import { InputConverter } from '../../inputConverter';
import { ILoggingService } from '../../services/interfaces/ILoggingService';
import { LayerService } from '../../services/layer.service';
import { IRoutingDetail, LogLevel } from '../../types';
import { BaseComponent } from '../base.component';

let Graphic: NonNullable<__esri.GraphicConstructor>;
let RouteParameters: NonNullable<__esri.RouteParametersConstructor>;
let FeatureSet: NonNullable<__esri.FeatureSetConstructor>;
let TextSymbol: NonNullable<__esri.TextSymbolConstructor>;

/* tslint:disable:angular-whitespace */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-routing',
    templateUrl: './routing.component.html',
    styleUrls: ['../sharedStyles/base.scss', './routing.component.scss']
})
export class RoutingComponent extends BaseComponent implements OnInit {
    @Input() barrierSymbol: any;
    @Input() stopSymbol: any;
    @Input() routeSymbol: any;
    @Input() unit: string = 'Miles';
    @Input() routingLayer: NonNullable<__esri.GraphicsLayer>;
    @Input() result: any;
    @Input() hideCreateLayerButton: boolean = false;
    @Input() hideStartOverButton: boolean = false;
    @Input() hideDirections: boolean = false;

    @Output() readonly routeComplete: EventEmitter<any> = new EventEmitter<any>();

    units: string[] = ['Miles', 'Kilometers', 'Feet', 'Meters', 'Yards', 'Nautical Miles'];

    routingEventHandler;
    mapView: NonNullable<__esri.MapView>;
    routingHighlightLayer: NonNullable<__esri.GraphicsLayer>;
    route: NonNullable<__esri.route>;
    routeUrl: string;
    routeParams: NonNullable<__esri.RouteParameters>;
    barrierUrl: string = `${environment.apiBaseUrl}/assets/images/barrier.png`;
    directions: any;
    distance: number;
    distanceInMiles: number;
    AddingType: string = 'stop';
    highlightedGraphic: any;

    @Input() @InputConverter() set active(value: boolean) {
        this._active = value;
        value ? this.activate() : this.deactivate();
    }
    get active(): boolean { return this._active; }
    private _active: boolean = false;

    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly layerService: LayerService,
        @Inject('LoggingService') public loggingService: ILoggingService
    ) {
        super(_vcr);
    }

    async ngOnInit() {
        try {
            this.mapView = await this.setupMapViewRef(this.mapView);

            [Graphic, RouteParameters, FeatureSet, TextSymbol] = await Promise.all([
                ESRI.Graphic,
                ESRI.RouteParameters,
                ESRI.FeatureSet,
                ESRI.TextSymbol
            ]);

            this.route = await ESRI.route;
            this.routeUrl = 'https://route-api.arcgis.com/arcgis/rest/services/World/Route/NAServer/Route_World';

            this.routeParams = new RouteParameters({
                apiKey: environment.routingApiKey,
                stops: new FeatureSet(),
                pointBarriers: new FeatureSet(),
                polylineBarriers: new FeatureSet(),
                polygonBarriers: new FeatureSet(),
                outSpatialReference: this.mapView.spatialReference,
                returnDirections: true,
                returnStops: true,
                returnBarriers: true,
                returnPolylineBarriers: true,
                returnPolygonBarriers: true
            });

            this.barrierSymbol = {
                type: 'picture-marker',
                url: this.barrierUrl,
                height: '18px',
                width: '18px'
            };

            this.stopSymbol = new TextSymbol({
                color: 'green',
                text: '\ue61d',
                font: {
                    size: 24,
                    family: 'CalciteWebCoreIcons'
                }
            });

            this.routeSymbol = {
                type: 'simple-line',
                color: '#0079c1',
                width: 5
            };

            this.routingHighlightLayer = await this.layerService.addGraphicsLayer({ title: 'Routing Highlight', hideInLegend: true });
        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
        }
    }

    @Input() public activate = () => {
        this._active = true;
        if (!this.routingEventHandler) {
            this.routingEventHandler = this.mapView.on('click', this.clickHandler.bind(this));
        }
    }

    @Input() public deactivate = () => {
        this._active = false;
        if (this.routingEventHandler) {
            this.routingEventHandler.remove();
            this.routingEventHandler = undefined;
        }
        this.startOver();
    }

    async clickHandler(event) {
        if (this.active) {
            if (this.AddingType === 'stop') {
                await this.addGraphic(event.mapPoint, this.stopSymbol, this.AddingType);
            } else if (this.AddingType === 'barrier') {
                await this.addGraphic(event.mapPoint, this.barrierSymbol, this.AddingType);
            }
        }
    }

    @Input() public addExternalGraphic = async (type, geometry, symbol?) => {
        geometry.spatialReference = this.mapView.spatialReference;
        if (type === 'stop') {
            await this.addGraphic(geometry, this.stopSymbol, type);
        } else if (type === 'barrier') {
            await this.addGraphic(geometry, this.barrierSymbol, type);
        } else {
            await this.addGraphic(geometry, symbol, type);
        }
    }

    async addGraphic(geometry, symbol, type) {
        if (!this.routingLayer) {
            try {
                this.routingLayer = await this.layerService.addGraphicsLayer({ title: 'Routing', hideInLegend: true });
            } catch (error) {
                this.loggingService.Log(error, LogLevel.Error);
            }
        }

        this.clearRoutes();
        this.clearHighlightLayer();
        const graphic = new Graphic({
            attributes: { Type: type },
            geometry,
            symbol
        });

        this.routingLayer.add(graphic);

        switch (type) {
            case 'stop':
                (this.routeParams.stops as any).features.push(graphic);
                break;

            case 'barrier':
                (this.routeParams.pointBarriers as any).features.push(graphic);
                break;

            case 'segment':
                (this.routeParams.polylineBarriers as any).features.push(graphic);
                break;

            case 'area':
                (this.routeParams.polygonBarriers as any).features.push(graphic);
                break;

            default:
                break;
        }

        await this.solveRoute();
        this.changeDetectorRef.detectChanges();
    }

    async removeGraphic(graphic, type) {
        this.routingLayer.remove(graphic);
        this.clearRoutes();
        this.clearHighlightLayer();

        switch (type) {
            case 'stop':
                const stopFeatures = (this.routeParams.stops as any).features.filter(x => x !== graphic);
                (this.routeParams.stops as any).features = stopFeatures;
                break;

            case 'barrier':
                const barrierFeatures = (this.routeParams.pointBarriers as any).features.filter(x => x !== graphic);
                (this.routeParams.pointBarriers as any).features = barrierFeatures;
                break;

            case 'segment':
                const segmentFeatures = (this.routeParams.polylineBarriers as any).features.filter(x => x !== graphic);
                (this.routeParams.polylineBarriers as any).features = segmentFeatures;
                break;

            case 'area':
                const areaFeatures = (this.routeParams.polygonBarriers as any).feature.filter(x => x !== graphic);
                (this.routeParams.polygonBarriers as any).features = areaFeatures;
                break;

            default:
                break;
        }

        await this.solveRoute();
        this.changeDetectorRef.detectChanges();
    }

    async overGraphic(graphic) {
        const layerView = await this.mapView.whenLayerView(this.routingLayer);
        this.highlightedGraphic = layerView.highlight(graphic);
    }

    outGraphic() {
        if (this.highlightedGraphic) {
            this.highlightedGraphic.remove();
        }
    }

    async drop(event: CdkDragDrop<any>) {
        moveItemInArray((this.routeParams.stops as any).features, event.previousIndex, event.currentIndex);
        this.clearRoutes();
        await this.solveRoute();
    }

    async solveRoute() {
        try {
            if ((this.routeParams.stops as any).features.length >= 2) {
                const data = await this.route.solve(this.routeUrl, this.routeParams);
                if (data) {
                    const routeResult = (data as any).routeResults[0];
                    routeResult.route.symbol = this.routeSymbol;
                    routeResult.route.attributes.Type = 'route';
                    this.result = data;
                    this.directions = routeResult.directions;
                    this.routingLayer.add(routeResult.route);
                    this.distanceInMiles = routeResult.route.attributes.Total_Miles;
                    this.mapView.goTo(routeResult.route.geometry, { animate: true, duration: 1000 });
                    this.unitChange(this.unit);
                }
            }
        } catch (error) {
            this.loggingService.Log(error, LogLevel.Error);
        }
    }

    zoomToLocation(direction) {
        this.highlightSegment(direction);
        this.mapView.goTo(direction.geometry, { animate: true });
    }

    clearHighlightLayer() {
        if (this.routingHighlightLayer && this.layerService.mapLayers.graphics.filter(x => x.title === this.routingHighlightLayer.title)[0]) {
            this.routingHighlightLayer.removeAll();
        }
    }

    async highlightSegment(segment) {
        this.clearHighlightLayer();
        this.layerService.moveLayerToTheTop(this.routingHighlightLayer);
        this.routingHighlightLayer.add(new Graphic({
            geometry: segment.geometry,
            symbol: {
                type: 'simple-line',
                color: 'rgba(0, 94, 149, 0.5)',
                width: 10
            } as __esri.SymbolProperties
        }));
    }

    startOver() {
        this.clearGraphics('stop');
        this.clearGraphics('barrier');
        this.clearGraphics('segment');
        this.clearGraphics('area');
        this.clearRoutes();
        this.clearHighlightLayer();
    }

    clearGraphics(type) {
        if (this.routingLayer) {
            const graphics = this.routingLayer.graphics.filter(x => x.attributes.Type === type);
            this.routingLayer.removeMany(graphics.toArray());
        }

        if (this.routeParams) {
            switch (type) {
                case 'stop':
                    this.routeParams.stops = new FeatureSet();
                    break;

                case 'barrier':
                    this.routeParams.pointBarriers = new FeatureSet();
                    break;

                case 'segment':
                    this.routeParams.polylineBarriers = new FeatureSet();
                    break;

                case 'area':
                    this.routeParams.polygonBarriers = new FeatureSet();
                    break;

                default:
                    break;
            }
        }
    }

    clearRoutes() {
        if (this.routingLayer) {
            const routes = this.routingLayer.graphics.filter(x => x.attributes.Type === 'route');
            this.routingLayer.removeMany(routes.toArray());
        }

        this.result = null;
        this.directions = null;
        this.distance = null;
        this.distanceInMiles = null;
    }

    async createLayer() {
        const routingDetail = await this.createRoutingDetail();
        const newRoutingLayer = await this.layerService.addGraphicsLayer({ title: 'Route', symbols: [this.stopSymbol, this.routeSymbol] }, { routingDetail });
        newRoutingLayer.addMany(this.routingLayer.graphics.toArray().filter(g => g.geometry.type === 'polyline'));
        this.routeComplete.emit(newRoutingLayer);
        this.startOver();
    }

    createRoutingDetail() {
        const routingDetail: IRoutingDetail = {
            distance: this.distance,
            distanceInMiles: this.distanceInMiles,
            unit: this.unit,
            pointBarriers: { features: [] },
            stops: { features: [] },
            directions: { features: [] }
        };

        if (this.routeParams.stops && (this.routeParams.stops as any).features.length > 0) {
            (this.routeParams.stops as any).features.forEach(stop => {
                const tempStop = {
                    geometry: {
                        latitude: stop.geometry.latitude,
                        longitude: stop.geometry.longitude
                    }
                };
                routingDetail.stops.features.push(tempStop);
            });
        }

        if (this.routeParams.pointBarriers) {
            (this.routeParams.pointBarriers as any).features.forEach(barrier => {
                const tempBarrier = {
                    geometry: {
                        latitude: barrier.geometry.latitude,
                        longitude: barrier.geometry.longitude
                    },
                    symbol: {
                        url: barrier.symbol.url
                    }
                };
                routingDetail.pointBarriers.features.push(tempBarrier);
            });
        }

        if (this.directions) {
            this.directions.features.forEach(direction => {
                const tempDirection = {
                    attributes: {
                        length: direction.attributes.length,
                        lengthInMiles: direction.attributes.lengthInMiles,
                        time: direction.attributes.time,
                        text: direction.attributes.text,
                        ETA: direction.attributes.ETA,
                        arriveTimeUTC: direction.attributes.arriveTimeUTC,
                        maneuverType: direction.attributes.maneuverType
                    }
                };
                routingDetail.directions.features.push(tempDirection);
            });
        }
        return routingDetail;
    }

    unitChange(unit) {
        this.unit = unit;
        if (this.distanceInMiles) {
            this.distance = this.convertFromMile(this.distanceInMiles, unit);
        }
        if (this.directions && this.directions.features) {
            this.directions.features.forEach(direction => {
                if (direction.attributes.length) {
                    if (!direction.attributes.lengthInMiles) {
                        const lengthInMilesProperty = { lengthInMiles: direction.attributes.length };
                        Object.assign(direction.attributes, lengthInMilesProperty);
                    }
                    direction.attributes.length = this.convertFromMile(direction.attributes.lengthInMiles, unit);
                }
            });
        }
    }

    convertFromMile(fromValue, fromUnit) {
        switch (fromUnit.toUpperCase()) {
            case 'MILES':
                return fromValue * 1;
                break;
            case 'KILOMETERS':
                return fromValue * 1.60934;
                break;
            case 'FEET':
                return fromValue * 5280;
                break;
            case 'METERS':
                return fromValue * 1609.34;
                break;
            case 'YARDS':
                return fromValue * 1760;
                break;
            case 'NAUTICAL MILES':
                return fromValue * 0.868976;
                break;
            default:
                break;
        }
    }
}
